/**
 * Franchise tabs + pills: homepage "Shop by Franchise" widget.
 *
 * - Main tabs and, within them, pill sub-tabs are both role="tab" widgets
 *   with manual activation: arrow keys/Home/End move focus and roving
 *   tabindex only; Enter/Space activate via the browser's native <button>
 *   click behavior, so no extra key handling is needed for that part.
 * - Only the first tab's first ("All") grid is rendered server-side. Every
 *   other grid is fetched on first activation via the Section Rendering API
 *   and cached by URL for the rest of the page's lifetime.
 * - No collection handles are hardcoded here -- every URL/handle this file
 *   touches comes from a data-collection-url / data-collection-handle
 *   attribute rendered server-side by Liquid.
 */
(function () {
  'use strict';

  var gridHtmlCache = new Map();

  function skeletonHtml() {
    var html = '<div class="franchise-tabs__skeleton" aria-hidden="true">';
    for (var i = 0; i < 8; i++) {
      html += '<div class="franchise-tabs__skeleton-card"></div>';
    }
    html += '</div>';
    return html;
  }

  function fetchGridHtml(url) {
    if (gridHtmlCache.has(url)) {
      return Promise.resolve(gridHtmlCache.get(url));
    }
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return fetch(url + sep + 'section_id=franchise-tab-grid')
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var inner = wrapper.querySelector('.franchise-tab-grid-section');
        var content = inner ? inner.innerHTML : html;
        gridHtmlCache.set(url, content);
        return content;
      });
  }

  function loadGrid(grid) {
    if (!grid) return;
    var url = grid.getAttribute('data-collection-url');
    var handle = grid.getAttribute('data-collection-handle');
    if (!url || !handle) return;
    if (grid.getAttribute('data-loaded-handle') === handle) return;

    if (gridHtmlCache.has(url)) {
      grid.innerHTML = gridHtmlCache.get(url);
      grid.setAttribute('data-loaded-handle', handle);
      return;
    }

    grid.innerHTML = skeletonHtml();
    fetchGridHtml(url)
      .then(function (html) {
        if (grid.getAttribute('data-collection-handle') === handle) {
          grid.innerHTML = html;
          grid.setAttribute('data-loaded-handle', handle);
        }
      })
      .catch(function () {
        if (grid.getAttribute('data-collection-handle') === handle) {
          grid.innerHTML =
            '<p class="franchise-tabs__empty">Couldn’t load this collection right now.</p>';
        }
      });
  }

  /**
   * Wires roving-tabindex keyboard behavior + click activation for one
   * role="tablist" group. onActivate receives the newly-activated tab.
   */
  function initTablist(tablist, onActivate) {
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    function activate(tab) {
      tabs.forEach(function (t) {
        var active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      if (typeof tab.scrollIntoView === 'function') {
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      onActivate(tab);
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        activate(tab);
      });
      tab.addEventListener('keydown', function (event) {
        var newIndex = null;
        if (event.key === 'ArrowRight') {
          newIndex = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft') {
          newIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          newIndex = 0;
        } else if (event.key === 'End') {
          newIndex = tabs.length - 1;
        }
        if (newIndex !== null) {
          event.preventDefault();
          tabs.forEach(function (t) {
            t.setAttribute('tabindex', '-1');
          });
          tabs[newIndex].setAttribute('tabindex', '0');
          tabs[newIndex].focus();
        }
        // Enter/Space activation is native <button> behavior (fires a
        // click event), so it needs no handling here. Arrow/Home/End only
        // move focus -- they do not activate the tab.
      });
    });
  }

  function initPanel(panel) {
    var grid = panel.querySelector('.franchise-tabs__grid');
    var pillList = panel.querySelector('.franchise-pills[role="tablist"]');
    if (pillList) {
      initTablist(pillList, function (pill) {
        if (!grid) return;
        grid.setAttribute('data-collection-handle', pill.getAttribute('data-collection-handle'));
        grid.setAttribute('data-collection-url', pill.getAttribute('data-collection-url'));
        loadGrid(grid);
      });
    }
  }

  function initFranchiseTabs(root) {
    var mainTablist = root.querySelector('.franchise-tabs__nav[role="tablist"]');
    var panels = Array.prototype.slice.call(root.querySelectorAll('.franchise-tabs__panel'));
    if (!mainTablist) return;

    panels.forEach(initPanel);

    initTablist(mainTablist, function (tab) {
      var targetId = tab.getAttribute('aria-controls');
      panels.forEach(function (panel) {
        var isTarget = panel.id === targetId;
        panel.classList.toggle('is-active', isTarget);
        if (isTarget) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
      var targetPanel = document.getElementById(targetId);
      if (targetPanel) {
        loadGrid(targetPanel.querySelector('.franchise-tabs__grid'));
      }
    });
  }

  document.querySelectorAll('.franchise-tabs').forEach(initFranchiseTabs);
})();
